import { useCollectionId } from 'articleEditor/hooks/useCollectionId';
import {
  createPrimitiveComponent,
  findNode,
  useEditorSelection,
} from 'lib-knowledge-document-structure/plate/common';
import { useMyPlateEditorRef } from 'lib-knowledge-document-structure/plateHooks';
import {
  ELEMENT_INTERLINK,
  MyInterlinkElement,
} from 'lib-knowledge-document-structure/plateTypes';
import { routeGenerator } from 'lib-knowledge-utils/routes';
import { ElementType, PropsWithoutRef, ReactNode, useMemo } from 'react';

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const useInterlinkOpenButtonState = () => {
  const editor = useMyPlateEditorRef();
  const selection = useEditorSelection();

  const entry = useMemo(
    () =>
      findNode<MyInterlinkElement>(editor, {
        match: { type: ELEMENT_INTERLINK },
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [editor, selection],
  );

  if (!entry) {
    return {};
  }

  const [element] = entry;

  return {
    element,
  };
};

export const useInterlinkOpenButton = ({
  element,
}: {
  element?: MyInterlinkElement;
  // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
}) => {
  const collectionId = useCollectionId();
  if (!element) {
    return {
      props: {},
    };
  }

  return {
    props: {
      href: routeGenerator.articleEdit({
        articleId: element.linkId,
        collectionId: collectionId || undefined,
        languageId: element.langId,
      }),
      target: '_blank',
      'aria-label': 'Open link in a new tab',
      onMouseOver: (e: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
        e.stopPropagation();
      },
    },
  };
};

type InterlinkOpenButtonProps = {
  children?: ReactNode;
} & PropsWithoutRef<JSX.IntrinsicElements['a']>;

export const InterlinkOpenButton = createPrimitiveComponent<
  ElementType<JSX.IntrinsicElements['a'], keyof React.JSX.IntrinsicElements>,
  InterlinkOpenButtonProps
>('a')({
  stateHook: useInterlinkOpenButtonState,
  propsHook: useInterlinkOpenButton,
});
